import dayjs from 'dayjs';
import {makeObservable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import {
  type DateObj,
  type Timestamp,
  timestampToDateTimeObj,
  timestampToNaiveDateTimeObj,
} from '../../../utils/datetime';
import {getUuid} from '../../../utils/getUuid';
import {BaseModel} from '../Base/Base.model';

const ModelName = 'DateTimeModel';

export class DateTimeModel extends BaseModel {
  isUtc: boolean;
  value: number;
  private djsDate: dayjs.Dayjs;

  constructor(props: DateTimeModel.Props) {
    super({id: getUuid()});

    this.isUtc = props.isUtc ?? false;
    this.djsDate = dayjs(props.value, {utc: this.isUtc});
    this.value = this.djsDate.valueOf();

    makeObservable(
      this,
      // @ts-expect-error: value is number but we allow to pass string
      BaseModel.mobxAnnotationFactory<DateTimeModel, DateTimeModel.Props>(
        props,
      ),
    );
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  add(amount: number, unit: DateTimeModel.TimeUnit) {
    return new DateTimeModel({
      isUtc: this.isUtc,
      value: this.djsDate.add(amount, unit).valueOf(),
    });
  }

  override clone(): this {
    return new DateTimeModel({
      isUtc: this.isUtc,
      value: this.djsDate.valueOf(),
    }) as this;
  }

  diff(date: DateTimeModel, unit: DateTimeModel.TimeUnit) {
    return this.djsDate.diff(date.value, unit);
  }

  endOf(unit: DateTimeModel.TimeUnit) {
    return new DateTimeModel({
      isUtc: this.isUtc,
      value: this.djsDate.endOf(unit).valueOf(),
    });
  }

  format(template: string) {
    return this.djsDate.format(template);
  }

  getDayLabel(format = 'll') {
    const startOfDayDate = dayjs(undefined, {utc: this.isUtc}).startOf('day');
    const daysDiff = startOfDayDate.diff(this.djsDate.startOf('day'), 'day');

    switch (daysDiff) {
      case -1:
        /**
         * @i18n_parser
         *
         * t('date-time:weekdays.short')
         * t('date-time:weekdays.long')
         * t('date-time:months.short')
         * t('date-time:months.long')
         */
        return i18n.t('date-time:tomorrow');
      case 0:
        return i18n.t('date-time:today');
      case 1:
        return i18n.t('date-time:yesterday');
      default:
        return this.djsDate.format(format);
    }
  }

  gt(date: DateTimeModel, unit?: DateTimeModel.TimeUnit) {
    return dayjs(this.value).isAfter(date.value, unit);
  }

  gte(date: DateTimeModel, unit?: DateTimeModel.TimeUnit) {
    return dayjs(this.value).isSame(date.value, unit) || this.gt(date, unit);
  }

  inRange(
    start: DateTimeModel,
    end: DateTimeModel,
    unit?: DateTimeModel.TimeUnit,
  ) {
    return this.gt(start, unit) && this.lt(end, unit);
  }

  inRangeEq(
    start: DateTimeModel,
    end: DateTimeModel,
    unit?: DateTimeModel.TimeUnit,
  ) {
    return this.gte(start, unit) && this.lte(end, unit);
  }

  isSame(date: DateTimeModel, unit: 'date' | DateTimeModel.TimeUnit) {
    return this.djsDate.isSame(date.value, unit);
  }

  isSameDay(date: DateTimeModel) {
    return this.isSame(date, 'day');
  }

  lt(date: DateTimeModel, unit?: DateTimeModel.TimeUnit) {
    return dayjs(this.value).isBefore(date.value, unit);
  }

  lte(date: DateTimeModel, unit?: DateTimeModel.TimeUnit) {
    return dayjs(this.value).isSame(date.value, unit) || this.lt(date, unit);
  }

  startOf(unit: DateTimeModel.TimeUnit) {
    return new DateTimeModel({
      isUtc: this.isUtc,
      value: this.djsDate.startOf(unit).valueOf(),
    });
  }

  subtract(amount: number, unit: DateTimeModel.TimeUnit) {
    return new DateTimeModel({
      isUtc: this.isUtc,
      value: this.djsDate.subtract(amount, unit).valueOf(),
    });
  }

  toDate() {
    return this.djsDate.toDate();
  }

  toDateObj(): DateObj {
    const d = this.djsDate;

    return {
      _type: 'date',
      day: d.date(),
      month: d.month() + 1,
      year: d.year(),
    };
  }

  toDateTimeObj() {
    return timestampToDateTimeObj(this.value as Timestamp);
  }

  toISOString() {
    return this.djsDate.toISOString();
  }

  toNaiveDateTimeObj() {
    return timestampToNaiveDateTimeObj(this.value as Timestamp);
  }

  // @ts-expect-error: value is number but we allow to pass string
  override update(source: {isUtc?: boolean; value: DateTimeModel.Value}): this {
    const value = source.value ?? this.value;
    const isUtc = source.isUtc ?? this.isUtc;

    const nextDate = dayjs(value, {utc: isUtc});

    if (nextDate.isValid()) {
      runInAction(() => {
        this.isUtc = isUtc;
        this.djsDate = nextDate;
        this.value = nextDate.valueOf();
      });

      return this;
    } else {
      throw new Error(
        `DateTimeModel: Update failed! Invalid date value property ${value}`,
      );
    }
  }

  utc(keepLocalTime = false) {
    return new DateTimeModel({
      isUtc: true,
      value: this.djsDate.utc(keepLocalTime).valueOf(),
    });
  }
}

export namespace DateTimeModel {
  export type Props = BaseModel.ModelToProps<
    DateTimeModel,
    | 'add'
    | 'diff'
    | 'endOf'
    | 'format'
    | 'getDayLabel'
    | 'gt'
    | 'gte'
    | 'id'
    | 'inRange'
    | 'inRangeEq'
    | 'isSame'
    | 'isSameDay'
    | 'isUtc'
    | 'lt'
    | 'lte'
    | 'startOf'
    | 'subtract'
    | 'toDate'
    | 'toDateObj'
    | 'toDateTimeObj'
    | 'toISOString'
    | 'toNaiveDateTimeObj'
    | 'utc'
    | 'value'
  > & {
    isUtc?: boolean;
    value: Value;
  };

  export type TimeUnit =
    | 'day'
    | 'hour'
    | 'minute'
    | 'month'
    | 'second'
    | 'week'
    | 'year';

  export type Value = string | Timestamp;
}
